import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { completeSubscriberChecklistStep } from 'api/operations/electionService';
import { SubChecklistCard } from 'types/electionService';
import { ChecklistCompletionStatus } from 'types/enrollmentService';
import { FamilyMember } from 'store/modules/subscriber/subscriber-imq/types';
import { SubscriberImqModule } from 'store/modules/subscriber/subscriber-imq/subscriberImqModule';
import SubscriberTasksDelegate from 'store/modules/subscriber/subscriber-tasks/subscriberTasksDelegate';
import { ManageConditionModalDelegate } from './manageConditionModalDelegate';
import { HealthQuestionnaireDomainParams } from './types';

export default abstract class IHealthQuestionnairePage {
  protected readonly _domainParams: HealthQuestionnaireDomainParams;
  public readonly subscriberImqModule: SubscriberImqModule;
  public readonly subscriberTasksDelegate = new SubscriberTasksDelegate();
  public readonly manageConditionModalDelegate: ManageConditionModalDelegate;

  private _isInitialDataLoading = false;
  private _isActionLoading = false;

  public get domainParams(): HealthQuestionnaireDomainParams {
    return this._domainParams;
  }

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  public get canComplete(): boolean {
    return (
      !this.isInitialLoading &&
      this.subscriberImqModule.completedCategoriesCount === this.subscriberImqModule.totalCategoriesCount
    );
  }

  public get isStepCompleted(): boolean {
    return (
      this.subscriberTasksDelegate
        .getTaskChecklistOptions(this._domainParams.entityId)
        .find((option) => option.card === SubChecklistCard.HEALTH_QUESTIONNAIRE)?.completionStatus ===
      ChecklistCompletionStatus.COMPLETED
    );
  }

  public abstract get isInitialLoading(): boolean;

  public abstract editCategory(categoryName: string): void;

  protected abstract onCompleteRedirect(): void;

  public setIsInitialDataLoading(isInitialDataLoading: boolean): void {
    this._isInitialDataLoading = isInitialDataLoading;
  }

  protected setIsActionLoading(isActionLoading: boolean): void {
    this._isActionLoading = isActionLoading;
  }

  protected get isPageContentLoading(): boolean {
    return this._isInitialDataLoading;
  }

  public async getInitialData(): Promise<void> {
    this.setIsInitialDataLoading(true);
    await Promise.all([this.subscriberImqModule.getSubscriber(), this.subscriberImqModule.getRequiredSubInfo()]);
    runInAction(() => {
      this.subscriberImqModule.buildImqData();
      this.subscriberTasksDelegate.setTasks(this.subscriberImqModule.subscriber?.tasks ?? []);
      this.setIsInitialDataLoading(false);
    });
  }

  public async updateImqData(): Promise<void> {
    this.setIsActionLoading(true);
    await Promise.all([this.subscriberImqModule.getSubscriber(), this.subscriberImqModule.getRequiredSubInfo()]);
    runInAction(() => {
      this.subscriberImqModule.buildImqData();
      this.subscriberTasksDelegate.setTasks(this.subscriberImqModule.subscriber?.tasks ?? []);
      this.setIsActionLoading(false);
    });
  }

  public onManageCondition(category: string, condition: string, affectedPeople: FamilyMember[]): void {
    this.manageConditionModalDelegate.setupAndOpenModal(
      category,
      condition,
      affectedPeople,
      this.subscriberImqModule.familyCensus,
    );
  }

  public async completeChecklistStep(): Promise<void> {
    await completeSubscriberChecklistStep({
      subId: this._domainParams.subscriberId,
      entityId: this._domainParams.entityId,
      card: SubChecklistCard.HEALTH_QUESTIONNAIRE,
    });
    runInAction(() => {
      this.onCompleteRedirect();
    });
  }

  constructor(domainParams: HealthQuestionnaireDomainParams) {
    this._domainParams = domainParams;
    this.subscriberImqModule = new SubscriberImqModule({
      subscriberId: domainParams.subscriberId,
    });
    this.manageConditionModalDelegate = new ManageConditionModalDelegate({
      subscriberId: domainParams.subscriberId,
      refreshData: this.updateImqData.bind(this),
    });

    makeObservable<
      IHealthQuestionnairePage,
      '_isInitialDataLoading' | '_isActionLoading' | 'setIsActionLoading' | 'isPageContentLoading'
    >(this, {
      _isInitialDataLoading: observable,
      _isActionLoading: observable,
      domainParams: computed,
      isActionLoading: computed,
      canComplete: computed,
      isStepCompleted: computed,
      isPageContentLoading: computed,
      setIsInitialDataLoading: action.bound,
      setIsActionLoading: action.bound,
      getInitialData: action.bound,
      updateImqData: action.bound,
      onManageCondition: action.bound,
      completeChecklistStep: action.bound,
    });
  }
}
